/**
 * Sentry Integration Module
 * 
 * To enable Sentry error tracking:
 * 1. Install the package: npm install @sentry/react
 * 2. Set NEXT_PUBLIC_SENTRY_DSN in your .env file
 * 3. The integration will automatically activate in production
 */

// Mock Sentry object for development/when package not installed
const MockSentry = {
  init: (config: any) => {
    console.log('Sentry: Mock initialized with config:', config);
  },
  setUser: (user: any) => {
    console.log('Sentry: User context set:', user);
  },
  captureMessage: (message: string, level: string) => {
    console.log(`Sentry: Message captured [${level}]:`, message);
  },
  captureException: (error: Error) => {
    console.error('Sentry: Exception captured:', error);
  },
  addBreadcrumb: (breadcrumb: any) => {
    console.log('Sentry: Breadcrumb added:', breadcrumb);
  },
  Replay: class {
    constructor(_config: any) {}
  },
};

// ── Privacy scrubbers (used by beforeSend / beforeBreadcrumb) ─────────
// Defense-in-depth on top of Replay masking and `sendDefaultPii: false`.
// We belt-and-suspenders here because a single misconfigured fetch can
// otherwise leak credentials to Sentry.

const SENSITIVE_HEADER_NAMES = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'proxy-authorization',
]);

const SENSITIVE_QUERY_KEYS = new Set([
  'token',
  'access_token',
  'id_token',
  'refresh_token',
  'api_key',
  'apikey',
  'auth',
  'secret',
  'password',
  'session',
]);

function scrubHeadersInPlace(headers: Record<string, unknown> | undefined) {
  if (!headers || typeof headers !== 'object') return;
  for (const name of Object.keys(headers)) {
    if (SENSITIVE_HEADER_NAMES.has(name.toLowerCase())) {
      headers[name] = '[Filtered]';
    }
  }
}

function scrubUrl(url: string | undefined): string | undefined {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, 'https://placeholder.invalid');
    let changed = false;
    for (const key of Array.from(u.searchParams.keys())) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        u.searchParams.set(key, '[Filtered]');
        changed = true;
      }
    }
    return changed ? u.toString() : url;
  } catch {
    return url;
  }
}

function scrubBreadcrumb(breadcrumb: any) {
  if (!breadcrumb || typeof breadcrumb !== 'object') return breadcrumb;
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    const data = breadcrumb.data as Record<string, unknown>;
    if (typeof data.url === 'string') data.url = scrubUrl(data.url);
    scrubHeadersInPlace(data.headers as Record<string, unknown> | undefined);
    // Drop request/response bodies entirely from fetch/xhr breadcrumbs
    // — they may contain credentials, tokens, or PII.
    if ('request_body' in data) data.request_body = '[Filtered]';
    if ('response_body' in data) data.response_body = '[Filtered]';
  }
  return breadcrumb;
}

function scrubEvent(event: any) {
  if (!event || typeof event !== 'object') return event;
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = scrubUrl(event.request.url);
    }
    scrubHeadersInPlace(event.request.headers);
    if ('cookies' in event.request) event.request.cookies = '[Filtered]';
    if ('data' in event.request) event.request.data = '[Filtered]';
  }
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  }
  return event;
}

// Try to load real Sentry if available
let Sentry: any = MockSentry;
let sentryLoaded = false;

async function ensureSentryLoaded() {
  if (sentryLoaded) {
    return;
  }

  try {
    const sentryModule = await import('@sentry/react');
    Sentry = sentryModule;
    sentryLoaded = true;
    console.log('Sentry: Real SDK loaded');
  } catch {
    console.log('Sentry: SDK not installed, using mock');
  }
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export async function initSentry() {
  // Only initialize in production
  if (process.env.NODE_ENV !== 'production') {
    console.log('Sentry: Skipping initialization in development');
    return;
  }

  // Check if DSN is configured
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN || process.env.SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry: No DSN configured. Error tracking disabled.');
    return;
  }

  try {
    await ensureSentryLoaded();

    Sentry.init({
      dsn,
      integrations: [
        // Sentry SDK v8+ replaced class-based integrations
        // (`new BrowserTracing()`, `new Sentry.Replay({...})`) with
        // factory functions. The old API throws "is not a constructor"
        // at minified call sites in production builds.
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Privacy: mask all DOM text and block all media in replays.
          // Without these, replays ship every character users type
          // (emails, message bodies, form inputs) plus avatar/cover
          // images to a third party. See SECURITY_AUDIT.md C4.
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Performance Monitoring — sample at 10% in prod. 100% ships every
      // transaction including request URLs, query strings, and timing.
      tracesSampleRate: 0.1,

      // Session Replay — keep error sessions at 100% (they're rare and
      // valuable for debugging) but drop random sampling to 10%.
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      // Release tracking
      release: '1.0.0',

      // Environment
      environment: process.env.NODE_ENV || 'development',

      // Defense-in-depth: explicitly disable Sentry's default PII
      // capture (IP address, cookies, user-agent details).
      sendDefaultPii: false,

      // Strip sensitive headers and query params from breadcrumbs and
      // event payloads before they leave the browser.
      beforeBreadcrumb(breadcrumb: any) {
        return scrubBreadcrumb(breadcrumb);
      },

      // Filter expected errors and scrub the outgoing event.
      beforeSend(event: any, hint: any) {
        if (event.exception) {
          const error = hint.originalException;
          // Don't send cancelled requests or network timeouts.
          if (error?.message?.includes('AbortError')) return null;
          if (error?.message?.includes('Network request failed')) return null;
        }
        return scrubEvent(event);
      },
    });

    console.log('Sentry: Initialized successfully');
  } catch (error) {
    console.error('Sentry: Failed to initialize:', error);
  }
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id: string; email?: string; username?: string } | null) {
  try {
    Sentry.setUser(user);
  } catch (error) {
    console.error('Sentry: Failed to set user:', error);
  }
}

/**
 * Clear user context from Sentry
 */
export function clearSentryUser() {
  setSentryUser(null);
}

/**
 * Capture a custom event
 */
export function captureMessage(message: string, level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info') {
  try {
    Sentry.captureMessage(message, level);
  } catch (error) {
    console.error('Sentry: Failed to capture message:', error);
  }
}

/**
 * Capture an exception
 */
export function captureException(error: Error) {
  try {
    Sentry.captureException(error);
  } catch (e) {
    console.error('Sentry: Failed to capture exception:', e);
  }
}

/**
 * Add breadcrumb for better error context
 */
export function addBreadcrumb(breadcrumb: {
  message: string;
  category?: string;
  level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  data?: Record<string, any>;
}) {
  try {
    Sentry.addBreadcrumb(breadcrumb);
  } catch (error) {
    console.error('Sentry: Failed to add breadcrumb:', error);
  }
}
